import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Search, ExternalLink, FileText, ChevronLeft, ChevronRight, User, Inbox } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import SyncBar from '../components/SyncBar';
import PetanqueLoader from '../components/PetanqueLoader';
import InvoiceDetailDialog from '../components/InvoiceDetailDialog';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { api } from '../api/sangly';
import { money, moneyExact, fmtDate, PAYMENT } from '../lib/format';
import { cn } from '../lib/utils';
import usePollWhileActive from '../hooks/usePollWhileActive';

const PAGE = 50;

const STATUS_FILTERS = [
  { key: 'pending', label: 'To do' },   // draft (to post in Odoo) + not paid (to reimburse) · the default
  { key: 'draft', label: 'Draft' },
  { key: 'open', label: 'Not paid' },
  { key: 'paid', label: 'Paid' },
  { key: 'all', label: 'All' },
];

export default function ExpenseNotes() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState(searchParams.get('status') || 'pending');
  const [ordering, setOrdering] = useState('-ref');
  const [offset, setOffset] = useState(0);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  // Approved notes whose PDF is in Odoo Documents with no vendor bill yet: not
  // invoices (there is none to open), so they are listed apart, on top.
  const [waiting, setWaiting] = useState([]);

  const params = useMemo(() => {
    const p = { ndf: 'true', ordering, limit: PAGE, offset };
    if (q.trim()) p.q = q.trim();
    if (status !== 'all') p.status = status;
    return p;
  }, [q, status, ordering, offset]);

  const load = useCallback(async (soft = false) => {
    if (!soft) setLoading(true);
    try {
      const [d, w] = await Promise.all([api.invoices.list(params), api.expenseNotes.waiting().catch(() => [])]);
      setRows(d.results || []);
      setCount(d.count || 0);
      setWaiting(w);
    } finally { setLoading(false); }
  }, [params]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.users.list().then(setUsers).catch(() => {}); }, []);
  useEffect(() => { setOffset(0); }, [q, status, ordering]);
  usePollWhileActive(true, () => load(true), 20000);

  const from = count === 0 ? 0 : offset + 1;
  const to = Math.min(offset + PAGE, count);

  // Waiting notes belong to "To do" and "All", on the first page, and follow the search.
  const needle = q.trim().toLowerCase();
  const waitingShown = (status === 'pending' || status === 'all') && offset === 0
    ? waiting.filter((w) => !needle || [w.ref, w.employee, w.hr?.employee, w.hr?.title].some((v) => (v || '').toLowerCase().includes(needle)))
    : [];

  return (
    <AppLayout
      title="Expense Notes"
      description="Employee expense notes (notes de frais) — vendor bills referenced NDF-…, synced from Odoo, drafts included, plus notes still waiting in Odoo Documents"
      syncBar={<SyncBar onSynced={() => load(true)} />}
    >
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="flex flex-wrap gap-1.5">
          {STATUS_FILTERS.map((f) => (
            <button key={f.key} onClick={() => setStatus(f.key)}
              className={cn('rounded-md border px-2.5 py-1 text-[12px] font-medium transition-colors',
                status === f.key ? 'border-primary bg-primary/12 text-foreground' : 'border-border bg-card text-muted-foreground hover:bg-muted')}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Reference, employee, bill #…"
                 className="h-9 w-full pl-8 sm:w-64" />
        </div>
        <Select className="h-9 w-full sm:ml-auto sm:w-44" value={ordering} onChange={(e) => setOrdering(e.target.value)}>
          <option value="-ref">Reference ↓</option>
          <option value="ref">Reference ↑</option>
          <option value="-invoice_amount">Amount ↓</option>
          <option value="invoice_amount">Amount ↑</option>
          <option value="-due">Date ↓</option>
          <option value="supplier">Employee A–Z</option>
        </Select>
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-card shadow-card">
        <Table className="min-w-[1100px]">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Reference</TableHead>
              <TableHead>Employee</TableHead>
              <TableHead>Bill</TableHead>
              <TableHead>Bill date</TableHead>
              <TableHead>Filed</TableHead>
              <TableHead>Approved</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Links</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={9} className="h-40 text-center">
                <PetanqueLoader size={34} roll className="mx-auto" />
              </TableCell></TableRow>
            )}
            {!loading && waitingShown.map((w) => (
              <TableRow key={`waiting-${w.ref}`} className="bg-sky-50/50 hover:bg-sky-50/80">
                <TableCell className="font-mono font-medium">
                  {w.ref}
                  {w.hr?.title && (
                    <div className="max-w-[220px] truncate font-sans text-[11px] font-normal text-muted-foreground" title={w.hr.title}>
                      {w.hr.title}
                    </div>
                  )}
                </TableCell>
                <TableCell className="font-medium">
                  {w.hr?.employee || w.employee || '-'}
                  {w.hr?.department && <div className="text-[11px] font-normal text-muted-foreground">{w.hr.department}</div>}
                </TableCell>
                <TableCell className="text-[12px] italic text-muted-foreground">no bill yet</TableCell>
                <TableCell className="text-muted-foreground">-</TableCell>
                <TableCell className="text-muted-foreground">{w.hr?.submitted_at ? fmtDate(w.hr.submitted_at) : '-'}</TableCell>
                <TableCell className="text-muted-foreground" title={w.hr?.approved_by ? `by ${w.hr.approved_by}` : undefined}>
                  {w.hr?.approved_at ? fmtDate(w.hr.approved_at) : '-'}
                </TableCell>
                <TableCell className="text-right font-semibold tnum whitespace-nowrap"
                           title={(w.hr?.amount_tvac ?? w.amount) != null ? moneyExact(w.hr?.amount_tvac ?? w.amount, 'EUR') : undefined}>
                  {(w.hr?.amount_tvac ?? w.amount) != null ? money(w.hr?.amount_tvac ?? w.amount, 'EUR') : '-'}
                </TableCell>
                <TableCell>
                  <Badge variant="info"><Inbox className="mr-1 h-3 w-3" />Waiting in Odoo</Badge>
                  <div className="mt-0.5 text-[11px] text-muted-foreground">
                    in Documents{w.received_at ? ` since ${fmtDate(w.received_at)}` : ''} · create the vendor bill
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2 text-muted-foreground">
                    {w.hr?.url && (
                      <a href={w.hr.url} target="_blank" rel="noreferrer"
                         className="rounded p-0.5 transition-colors hover:bg-muted hover:text-foreground" title="Open the note in the HR portal">
                        <User className="h-3.5 w-3.5" />
                      </a>
                    )}
                    {w.document_url && (
                      <a href={w.document_url} target="_blank" rel="noreferrer"
                         className="rounded p-0.5 transition-colors hover:bg-muted hover:text-foreground" title="Open the document in Odoo, then Create Vendor Bill">
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!loading && rows.length === 0 && waitingShown.length === 0 && (
              <TableRow><TableCell colSpan={9} className="h-40 text-center text-muted-foreground">
                {q.trim() ? 'No expense notes match your search.' : status === 'pending' ? 'Nothing to do · every note is posted and reimbursed.' : 'No expense notes here.'}
              </TableCell></TableRow>
            )}
            {!loading && rows.map((inv) => (
              <TableRow key={inv.id} className="cursor-pointer" onClick={() => navigate(`/expense-notes/${inv.id}`)}>
                <TableCell className="font-mono font-medium">
                  {inv.ref || '-'}
                  {inv.hr?.title && (
                    <div className="max-w-[220px] truncate font-sans text-[11px] font-normal text-muted-foreground" title={inv.hr.title}>
                      {inv.hr.title}
                    </div>
                  )}
                </TableCell>
                {/* The employee comes from the HR portal: the vendor on an Odoo draft is
                    whatever the document reader took off the receipt. */}
                <TableCell className="font-medium">
                  {inv.hr?.employee || inv.supplier || '-'}
                  {inv.hr?.department && <div className="text-[11px] font-normal text-muted-foreground">{inv.hr.department}</div>}
                </TableCell>
                <TableCell className="font-mono text-[12px] text-muted-foreground">
                  {inv.payment_state === 'draft' ? <span className="font-sans italic">not posted</span> : (inv.name || '-')}
                </TableCell>
                <TableCell className="text-muted-foreground">{fmtDate(inv.invoice_date)}</TableCell>
                <TableCell className="text-muted-foreground">{inv.hr?.submitted_at ? fmtDate(inv.hr.submitted_at) : '-'}</TableCell>
                <TableCell className="text-muted-foreground" title={inv.hr?.approved_by ? `by ${inv.hr.approved_by}` : undefined}>
                  {inv.hr?.approved_at ? fmtDate(inv.hr.approved_at) : '-'}
                </TableCell>
                {/* The note's own total, from the HR portal · Odoo's figure on a draft is
                    whatever the document reader added up, and can be one receipt short. */}
                <TableCell className="text-right font-semibold tnum whitespace-nowrap"
                           title={moneyExact(inv.hr?.amount_tvac ?? inv.amount_total, inv.currency)}>
                  {money(inv.hr?.amount_tvac ?? inv.amount_total, inv.currency)}
                  {inv.hr && Math.abs(Number(inv.hr.amount_tvac) - Number(inv.amount_total)) > 0.005 && (
                    <div className="text-[11px] font-normal text-rose-600" title="The bill in Odoo carries a different amount">
                      Odoo: {money(inv.amount_total, inv.currency)}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant={(PAYMENT[inv.payment_state] || PAYMENT.not_paid).variant}>
                    {(PAYMENT[inv.payment_state] || PAYMENT.not_paid).label}
                  </Badge>
                  {inv.payment_date && (inv.payment_state === 'in_payment' || inv.payment_state === 'paid') && (
                    <div className="mt-0.5 text-[11px] text-muted-foreground">
                      {inv.payment_state === 'in_payment' ? 'planned ' : 'paid '}{fmtDate(inv.payment_date)}
                    </div>
                  )}
                  {inv.payment_state === 'draft' && (
                    <div className="mt-0.5 text-[11px] text-muted-foreground">to validate in Odoo</div>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2 text-muted-foreground">
                    {inv.hr?.url && (
                      <a href={inv.hr.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                         className="rounded p-0.5 transition-colors hover:bg-muted hover:text-foreground" title="Open the note in the HR portal">
                        <User className="h-3.5 w-3.5" />
                      </a>
                    )}
                    {inv.pdf_url && (
                      <a href={inv.pdf_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                         className="rounded p-0.5 transition-colors hover:bg-muted hover:text-foreground" title="Open the bill PDF">
                        <FileText className="h-3.5 w-3.5" />
                      </a>
                    )}
                    {inv.odoo_url && (
                      <a href={inv.odoo_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                         className="rounded p-0.5 transition-colors hover:bg-muted hover:text-foreground" title="Open in Odoo">
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="mt-3 flex items-center justify-between text-[12px] text-muted-foreground">
        <span>
          {from}–{to} of {count.toLocaleString('nl-BE')}
          {waitingShown.length > 0 && ` · plus ${waitingShown.length} waiting in Odoo Documents`}
        </span>
        <div className="flex items-center gap-1">
          <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}
            className="inline-flex h-8 items-center gap-1 rounded-md border border-border bg-card px-2 disabled:opacity-40 hover:bg-muted">
            <ChevronLeft className="h-4 w-4" /> Prev
          </button>
          <button disabled={to >= count} onClick={() => setOffset(offset + PAGE)}
            className="inline-flex h-8 items-center gap-1 rounded-md border border-border bg-card px-2 disabled:opacity-40 hover:bg-muted">
            Next <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <InvoiceDetailDialog invoiceId={id} open={!!id} onClose={() => navigate('/expense-notes')}
                           onChanged={() => load(true)} users={users} />
    </AppLayout>
  );
}
